import BaseObject, { BaseObjectParams } from './baseObject';
import Clock from './clock';
import Scene from './scene/scene';
import Player from './player';
import Level from './level';
import RTSControls from './control/rtsControls';
import ManualControls from './control/manualControls';
import LevelSelectorScene from './scene/LevelSelectorScene';
import Cursor from './element/cursor';

import { Dictionary } from '../utils/types';
import { paths } from '../config/paths.config';


export interface GameParams extends BaseObjectParams {
  canvasWidth: number;
  canvasHeight: number;
  canvas?: Dictionary<HTMLCanvasElement>;
  contexts?: Dictionary<CanvasRenderingContext2D>;
}

enum CanvasTypes {
  BACKGROUND_CANVAS = 'backgroundCanvas',
  MAIN_CANVAS = 'mainCanvas',
  FOG_CANVAS = 'fogCanvas',
  FOREGROUND_CANVAS = 'foregroundCanvas',
  INTERFACE_CANVAS = 'interfaceCanvas',
}

export default class Game extends BaseObject implements GameParams {
  static instance: Game;

  canvasWidth!: number;
  canvasHeight!: number;
  canvasTypes: Array<CanvasTypes> = [
    CanvasTypes.BACKGROUND_CANVAS,
    CanvasTypes.MAIN_CANVAS,
    CanvasTypes.FOG_CANVAS,
    CanvasTypes.FOREGROUND_CANVAS,
    CanvasTypes.INTERFACE_CANVAS,
  ]
  canvas: Dictionary<HTMLCanvasElement>;
  contexts: Record<CanvasTypes, CanvasRenderingContext2D>;

  player: Player;
  scene!: Scene;
  level!: Level;
  clock: Clock;
  requestAnimationId!: number;
  paused = false;

  containerDOM!: HTMLDivElement;

  controlSchemes: {
    rts: RTSControls,
    manual: ManualControls,
  };
  cursor: Cursor;

  levelSelector?: LevelSelectorScene;

  constructor(params: GameParams) {
    super();
    this.applyParams(params);
    Game.instance = this;

    this.player = new Player({ game: this });
    this.clock = new Clock();

    this.canvas = {};
    this.contexts = {} as Record<CanvasTypes, CanvasRenderingContext2D>;

    window.addEventListener('blur', () => {
      this.paused = true;
    });

    this.initDOM();

    /* Cursor */
    this.cursor = new Cursor({
      context: this.contexts.foregroundCanvas,
      useLock: true,
      width: 25,
      height: 25,
      spriteParams: {
        src: `${paths.img.cursors}/cursor.gif`,
        offsetCenterX: 11,
        offsetCenterY: 10,
        ignoreScale: true,
      }
    }).create();

    /* Controls */
    this.controlSchemes = {
      rts: new RTSControls({
        context: Game.instance.contexts.foregroundCanvas,
      }),
      manual: new ManualControls({
        context: Game.instance.contexts.foregroundCanvas,
      })
    };

    this.tick();
  }

  tick(): Game {
    const secondsSinceLastFrame = this.clock.getSecondsSinceLastTick();
    if (this.scene)
      this.scene.tick(secondsSinceLastFrame);

    this.cursor.draw();

    /* Loop */
    this.requestAnimationId = window.requestAnimationFrame(() => {
      this.tick();
    });
    return this;
  }

  togglePause(): void {
    this.paused = !this.paused;
  }

  initDOM(): void {
    this.initContainer();
    this.addDOMCanvas();
    this.resize();
    window.addEventListener('resize', () => {
      this.resize();
    }, false);
  }

  initContainer(): void {
    const container = document.createElement('div');
    container.style.position = 'absolute';
    container.style.margin = 'auto';
    container.style.left = '0';
    container.style.top = '0';
    container.style.right = '0';
    container.style.bottom = '0';
    container.style.border = '0';
    container.id = 'game-container';
    this.containerDOM = container;
    document.querySelector('body')!.appendChild(container);
  }

  addDOMCanvas(): void {
    this.canvasTypes
      .forEach((canvasName: CanvasTypes) => {
        const newCanvas: HTMLCanvasElement = document.createElement('canvas');
        newCanvas.width = this.canvasWidth;
        newCanvas.height = this.canvasHeight;
        newCanvas.setAttribute('id', canvasName);
        newCanvas.style.width = '100%';
        newCanvas.style.height = '100%';
        newCanvas.style.position = 'absolute';
        this.containerDOM.appendChild(newCanvas);

        const context = newCanvas.getContext('2d');
        if (context) {
          this.contexts[canvasName] = context;
          this.canvas[canvasName] = newCanvas;
        }
      })
  }

  resize(): void {
    const gameWidth: number = window.innerWidth;
    const gameHeight: number = window.innerHeight;
    const scaleToFitX: number = gameWidth / this.canvasWidth;
    const scaleToFitY: number = gameHeight / this.canvasHeight;
    const currentScreenRatio: number = gameWidth / gameHeight;
    const optimalRatio: number = Math.min(scaleToFitX, scaleToFitY);

    const isFullScreen = (currentScreenRatio >= 1.77 && currentScreenRatio <= 1.79);
    if (isFullScreen) {
      this.containerDOM.style.width = '100%';
      this.containerDOM.style.height = '100%';
    }
    else {
      this.containerDOM.style.width = this.canvasWidth * optimalRatio + 'px';
      this.containerDOM.style.height = this.canvasHeight * optimalRatio + 'px';
    }
  }

  clearAll(): void {
    (Object.keys(this.contexts) as CanvasTypes[])
      .forEach(key => {
        const ctx = this.contexts[key];
        ctx.save();
        ctx.setTransform(
          1, 0, 0,
          1, 0, 0,
        );
        ctx.clearRect(
          0, 0,
          this.canvasWidth, this.canvasWidth,
        );
        ctx.restore();
      })
  }
}
